import React, { useEffect, useState, useCallback, useRef, useContext } from "react";
import { Button, Col, Row, Spinner } from "reactstrap";
import { differenceInCalendarDays, format, isSameMonth, parse } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast } from "react-toastify";
import { PostData, DownloadBlob } from "../../../services/FetchData";
import DateRangePickerComponent from "../../../components/DaterangePicker";
import AdminDashboardHeader from "../../../components/AdminDashboard/AdminDashboardHeader";
import AiAssistantAnalyticsTable from "../../../components/AdminDashboard/AiAssistantAnalyticsTable";
import { ProductContext } from "../../../store";

const PAGE_SIZE = 50;
const SCROLL_TARGET_ID = "mainContentScroll";
const MAX_RANGE_DAYS = 31;

const parseApiDate = (dateStr) => parse(dateStr, "yyyy-MM-dd", new Date());

const dateRangeFromApiPeriod = (currentPeriod) => {
	if (!currentPeriod?.startDate || !currentPeriod?.endDate) {
		return null;
	}
	return {
		startDate: parseApiDate(currentPeriod.startDate),
		endDate: parseApiDate(currentPeriod.endDate),
		key: "selection",
	};
};

const validateAdminDateRange = (range) => {
	const { startDate, endDate } = range;
	if (!startDate || !endDate) {
		return "Please select a date range";
	}
	if (startDate > endDate) {
		return "Start date must be before end date";
	}
	if (endDate > new Date()) {
		return "End date cannot be in the future";
	}
	if (!isSameMonth(startDate, endDate)) {
		return "Date range must stay within a single month";
	}
	const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
	if (dayCount > MAX_RANGE_DAYS) {
		return "Date range cannot exceed one month";
	}
	return null;
};

const AiAssistantDashboardComparison = () => {
	const { dateRange, setDateRange } = useContext(ProductContext);
	const [useCustomRange, setUseCustomRange] = useState(false);
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState(null);
	const [hasMore, setHasMore] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [exportingKey, setExportingKey] = useState(null);
	const fetchInFlightRef = useRef(false);
	const pageRef = useRef(1);
	const initialRangeSyncedRef = useRef(false);
	const fetchPageRef = useRef(null);

	const buildRequestPayload = useCallback(
		(pageNum) => {
			if (useCustomRange) {
				return {
					startDate: format(dateRange.startDate, "yyyy-MM-dd"),
					endDate: format(dateRange.endDate, "yyyy-MM-dd"),
					page: pageNum,
					limit: PAGE_SIZE,
				};
			}
			return {
				referenceDate: format(new Date(), "yyyy-MM-dd"),
				page: pageNum,
				limit: PAGE_SIZE,
			};
		},
		[dateRange, useCustomRange]
	);

	const syncDateRangeFromApi = useCallback((payload, append) => {
		if (append || useCustomRange || initialRangeSyncedRef.current) {
			return;
		}
		const nextRange = dateRangeFromApiPeriod(payload.currentPeriod);
		if (!nextRange) {
			return;
		}
		setDateRange(nextRange);
		initialRangeSyncedRef.current = true;
	}, [useCustomRange, setDateRange]);

	const fetchPage = useCallback(
		async (pageNum, append = false) => {
			if (fetchInFlightRef.current) return;

			if (useCustomRange) {
				const rangeError = validateAdminDateRange(dateRange);
				if (rangeError) {
					if (!append) {
						setError(rangeError);
						setLoading(false);
					}
					toast.error(rangeError);
					return;
				}
			}

			fetchInFlightRef.current = true;

			if (append) {
				setLoadingMore(true);
			} else {
				setLoading(true);
				setError(null);
			}

			try {
				const result = await PostData(
					"ms1",
					"api/v2/admin/analytics/aiAssistant/fullStats",
					buildRequestPayload(pageNum)
				);

				if (result !== "Invalid" && result?.status === "success") {
					const payload = result.data ?? {};
					syncDateRangeFromApi(payload, append);
					setMeta((prev) =>
						append
							? prev
							: {
								currentPeriod: payload.currentPeriod,
								previousPeriod: payload.previousPeriod,
								comparisonMode: payload.comparisonMode,
								pagination: payload.pagination,
								sortBy: payload.sortBy,
							}
					);
					setRows((prev) =>
						append ? [...prev, ...(payload.rows ?? [])] : payload.rows ?? []
					);
					setHasMore(payload.pagination?.hasMore ?? false);
					pageRef.current = pageNum;
				} else {
					const message =
						result?.message?.message ||
						result?.message ||
						"Failed to load AI Assistant dashboard";
					if (!append) {
						setError(message);
					}
					toast.error(message);
				}
			} catch (err) {
				const message = "Failed to load AI Assistant dashboard";
				if (!append) {
					setError(message);
				}
				toast.error(message);
			} finally {
				setLoading(false);
				setLoadingMore(false);
				fetchInFlightRef.current = false;
			}
		},
		[buildRequestPayload, dateRange, useCustomRange, syncDateRangeFromApi]
	);

	fetchPageRef.current = fetchPage;

	const handleDownloadMetric = useCallback(
		async ({ websiteId, metricKey, label }) => {
			if (!websiteId || !metricKey || exportingKey) return;

			const exportKey = `${websiteId}:${metricKey}`;
			setExportingKey(exportKey);

			let payload;
			if (useCustomRange) {
				const rangeError = validateAdminDateRange(dateRange);
				if (rangeError) {
					toast.error(rangeError);
					setExportingKey(null);
					return;
				}
				payload = {
					websiteId,
					metricKey,
					startDate: format(dateRange.startDate, "yyyy-MM-dd"),
					endDate: format(dateRange.endDate, "yyyy-MM-dd"),
				};
			} else if (meta?.currentPeriod?.startDate && meta?.currentPeriod?.endDate) {
				payload = {
					websiteId,
					metricKey,
					startDate: meta.currentPeriod.startDate,
					endDate: meta.currentPeriod.endDate,
				};
			} else {
				payload = {
					websiteId,
					metricKey,
					referenceDate: format(new Date(), "yyyy-MM-dd"),
				};
			}

			try {
				const { blob, filename } = await DownloadBlob(
					"ms1",
					"api/v2/admin/analytics/aiAssistantFullStatsMetricExport",
					payload
				);
				const url = window.URL.createObjectURL(blob);
				const anchor = document.createElement("a");
				anchor.href = url;
				anchor.download = filename || `${metricKey}_${websiteId}.xlsx`;
				document.body.appendChild(anchor);
				anchor.click();
				anchor.remove();
				window.URL.revokeObjectURL(url);
				toast.success(`${label || metricKey} data downloaded`);
			} catch (err) {
				toast.error(err?.message || `Failed to download ${label || metricKey}`);
			} finally {
				setExportingKey(null);
			}
		},
		[dateRange, useCustomRange, exportingKey, meta]
	);

	const loadMore = useCallback(() => {
		if (!loadingMore && hasMore && !fetchInFlightRef.current) {
			fetchPage(pageRef.current + 1, true);
		}
	}, [fetchPage, hasMore, loadingMore]);

	const handleDateRangeChange = useCallback((range) => {
		const rangeError = validateAdminDateRange(range);
		if (rangeError) {
			toast.error(rangeError);
			return;
		}
		initialRangeSyncedRef.current = true;
		setDateRange(range);
		setUseCustomRange(true);
	}, [setDateRange]);

	const retry = useCallback(() => {
		setRows([]);
		setMeta(null);
		pageRef.current = 1;
		setHasMore(false);
		if (!useCustomRange) {
			initialRangeSyncedRef.current = false;
		}
		fetchPage(1, false);
	}, [fetchPage, useCustomRange]);

	useEffect(() => {
		document.title = "AI Assistant | ConversionBox App";
		return () => {
			document.title = "ConversionBox App";
		};
	}, []);

	useEffect(() => {
		fetchPageRef.current?.(1, false);
	}, []);

	useEffect(() => {
		if (!useCustomRange) {
			return;
		}
		pageRef.current = 1;
		setRows([]);
		setMeta(null);
		setHasMore(false);
		fetchPageRef.current?.(1, false);
	}, [useCustomRange, dateRange.startDate, dateRange.endDate]);

	if (error && rows.length === 0 && !loading) {
		return (
			<div className="adminDashboardEmpty">
				<p>{error}</p>
				<Button color="primary" className="adminDashboardRetry" onClick={retry}>
					Retry
				</Button>
			</div>
		);
	}

	const total = meta?.pagination?.total;
	const loadedCount = rows.length;

	return (
		<div>
			<Row className="mt-3 align-items-start justify-content-between g-3">
				<Col xs="12" lg>
					<AdminDashboardHeader
						title="AI Assistant"
						currentPeriod={meta?.currentPeriod}
						previousPeriod={meta?.previousPeriod}
						comparisonMode={meta?.comparisonMode}
						loadedCount={loadedCount}
						totalCount={total}
						sortBy={meta?.sortBy}
					/>
				</Col>
				<Col xs="12" lg="auto" className="adminDashboardDatePickerCol">
					<DateRangePickerComponent
						dateRange={dateRange}
						onSelect={handleDateRangeChange}
					/>
				</Col>
			</Row>
			<InfiniteScroll
				dataLength={rows.length}
				next={loadMore}
				hasMore={hasMore}
				scrollableTarget={SCROLL_TARGET_ID}
				scrollThreshold={0.85}
				loader={
					<div className="adminDashboardLoadMore">
						<Spinner size="sm" color="primary" /> Loading more websites...
					</div>
				}
				endMessage={
					loadedCount > 0 ? (
						<div className="adminDashboardEndMessage">
							Showing all {loadedCount} website{loadedCount === 1 ? "" : "s"}
						</div>
					) : null
				}
			>
				<AiAssistantAnalyticsTable
					rows={rows}
					loading={loading}
					onDownloadMetric={handleDownloadMetric}
					exportingKey={exportingKey}
				/>
			</InfiniteScroll>
			{hasMore && !loading && (
				<div className="adminDashboardLoadMoreActions">
					<Button
						color="primary"
						outline
						onClick={loadMore}
						disabled={loadingMore}
					>
						{loadingMore ? (
							<>
								<Spinner size="sm" /> Loading...
							</>
						) : (
							`Load more (${loadedCount} of ${total ?? "..."})`
						)}
					</Button>
				</div>
			)}
		</div>
	);
};

export default AiAssistantDashboardComparison;
